import Moment from 'react-moment';
import {
  DatePicker,
  DayOfWeek,
  defaultDatePickerStrings
} from '@fluentui/react';
import { Button, MessageBar, MessageBarBody, Tooltip } from '@fluentui/react-components';
import {
  CalendarLtrRegular,
  CalendarCancelRegular,
  CheckmarkRegular,
  DismissRegular,
  EditRegular,
  HourglassRegular,
  TagRegular
} from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './PriceListInfoComponent.css';

import * as ownFunctions from '../AgreementPricelist/AgreementPriceListFunctions';
import { onFormatDate, parseUTCDateAsLocal } from '../../../commons/functions/dateUtils';
import { IAssociatedPriceList } from '../../../models/Entities/PriceList/IAssociatedPriceList';

export interface PriceListInfoPanelProps {
  priceList: IAssociatedPriceList | undefined;
  onConfirmStartValidity: (date: Date) => void;
  onConfirmEndValidity: (date: Date) => void;
  /** i18n root namespace: 'priceListInfo' | 'providerPriceListInfo' | 'tenantPriceListInfo' */
  i18nRoot?: string;
}

const firstDayOfWeek = DayOfWeek.Sunday;

export const PriceListInfoPanel = (props: PriceListInfoPanelProps) => {
  const { priceList, i18nRoot = 'priceListInfo' } = props;
  const [t] = useTranslation();

  const [activeDateFrom, setActiveDateFrom] = useState(false);
  const [activeDateTo, setActiveDateTo] = useState(false);
  const [validityDateStart, setValidityDateStart] = useState<Date>();
  const [validityDateEnd, setValidityDateEnd] = useState<Date>();
  const [validityErrors, setValidityErrors] = useState<string[]>([]);
  const [today, setToday] = useState<Date>(new Date());

  // Refresh "today" once per minute so the relative-time text doesn't go stale.
  useEffect(() => {
    const id = globalThis.setInterval(() => setToday(new Date()), 60_000);
    return () => globalThis.clearInterval(id);
  }, []);

  useEffect(() => {
    if (priceList) {
      setValidityDateStart(parseUTCDateAsLocal(priceList.effectiveDateStart));
      setValidityDateEnd(parseUTCDateAsLocal(priceList.effectiveDateEnd));
    }
  }, [priceList]);

  if (!priceList) return null;

  const validate = (): boolean => {
    const errors: string[] = [];
    if (validityDateStart && validityDateEnd && validityDateStart >= validityDateEnd) {
      errors.push(t(`${i18nRoot}.validityError.dateError`));
    }
    setValidityErrors(errors);
    return errors.length === 0;
  };

  const onConfirmStart = () => {
    if (validityDateStart && validate()) {
      props.onConfirmStartValidity(validityDateStart);
      setActiveDateFrom(false);
    }
  };

  const onConfirmEnd = () => {
    if (validityDateEnd && validate()) {
      props.onConfirmEndValidity(validityDateEnd);
      setActiveDateTo(false);
    }
  };

  const cancelStart = () => {
    setActiveDateFrom(false);
    setValidityErrors([]);
  };
  const cancelEnd = () => {
    setActiveDateTo(false);
    setValidityErrors([]);
  };

  const status = ownFunctions.GetPriceListStatus(priceList);
  const statusLabel = t(status.i18nKey);

  return (
    <div className="infoPanel">
      {validityErrors.length > 0 && (
        <div className="infoPanel-errorBar">
          <MessageBar intent="error">
            <MessageBarBody>
              <strong>{t([`${i18nRoot}.errorAttention`, `${i18nRoot}.attentionTitle`] as any)}</strong>
              <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                {validityErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      <div className="infoPanel-grid">
        {/* ── Valid from ── */}
        <div className={`infoPanel-section ${activeDateFrom ? 'editable' : ''}`}>
          <span className="infoPanel-icon" aria-hidden="true"><CalendarLtrRegular /></span>
          <div className="infoPanel-body">
            <span className="infoPanel-eyebrow">{t(`${i18nRoot}.validSince`)}</span>
            {activeDateFrom ? (
              <div className="infoPanel-row">
                <DatePicker
                  firstDayOfWeek={firstDayOfWeek}
                  placeholder={t(`${i18nRoot}.parameters.validityStart`)}
                  ariaLabel={t(`${i18nRoot}.validSince`)}
                  formatDate={onFormatDate}
                  onSelectDate={(d) => setValidityDateStart(d || new Date())}
                  strings={defaultDatePickerStrings}
                  value={validityDateStart}
                />
                <span className="infoPanel-actions">
                  <Tooltip content={t(`${i18nRoot}.parameters.confirmButton`)} relationship="label" withArrow>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<CheckmarkRegular />}
                      aria-label={t(`${i18nRoot}.parameters.confirmButton`)}
                      onClick={onConfirmStart}
                    />
                  </Tooltip>
                  <Tooltip content={t(`${i18nRoot}.parameters.cancelButton`)} relationship="label" withArrow>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DismissRegular />}
                      aria-label={t(`${i18nRoot}.parameters.cancelButton`)}
                      onClick={cancelStart}
                    />
                  </Tooltip>
                </span>
              </div>
            ) : (
              <div className="infoPanel-row">
                {priceList.effectiveDateStart ? (
                  <span className="infoPanel-value">
                    {onFormatDate(parseUTCDateAsLocal(priceList.effectiveDateStart))}
                  </span>
                ) : (
                  <span className="infoPanel-value muted">{t(`${i18nRoot}.parameters.notInformed`)}</span>
                )}
                <span className="infoPanel-actions">
                  <Tooltip content={t(`${i18nRoot}.parameters.editButton`)} relationship="label" withArrow>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<EditRegular />}
                      aria-label={t(`${i18nRoot}.parameters.editButton`)}
                      onClick={() => setActiveDateFrom(true)}
                    />
                  </Tooltip>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Valid until ── */}
        <div className={`infoPanel-section ${activeDateTo ? 'editable' : ''}`}>
          <span className="infoPanel-icon" aria-hidden="true"><CalendarCancelRegular /></span>
          <div className="infoPanel-body">
            <span className="infoPanel-eyebrow">{t(`${i18nRoot}.validUntil`)}</span>
            {activeDateTo ? (
              <div className="infoPanel-row">
                <DatePicker
                  firstDayOfWeek={firstDayOfWeek}
                  placeholder={t(`${i18nRoot}.parameters.endOfValidity`)}
                  ariaLabel={t(`${i18nRoot}.validUntil`)}
                  formatDate={onFormatDate}
                  onSelectDate={(d) => setValidityDateEnd(d || new Date())}
                  strings={defaultDatePickerStrings}
                  value={validityDateEnd}
                />
                <span className="infoPanel-actions">
                  <Tooltip content={t(`${i18nRoot}.parameters.confirmButton`)} relationship="label" withArrow>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<CheckmarkRegular />}
                      aria-label={t(`${i18nRoot}.parameters.confirmButton`)}
                      onClick={onConfirmEnd}
                    />
                  </Tooltip>
                  <Tooltip content={t(`${i18nRoot}.parameters.cancelButton`)} relationship="label" withArrow>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DismissRegular />}
                      aria-label={t(`${i18nRoot}.parameters.cancelButton`)}
                      onClick={cancelEnd}
                    />
                  </Tooltip>
                </span>
              </div>
            ) : (
              <div className="infoPanel-row">
                {priceList.effectiveDateEnd ? (
                  <span className="infoPanel-value">
                    {onFormatDate(parseUTCDateAsLocal(priceList.effectiveDateEnd))}
                  </span>
                ) : (
                  <span className="infoPanel-value muted">{t(`${i18nRoot}.parameters.notInformed`)}</span>
                )}
                <span className="infoPanel-actions">
                  <Tooltip content={t(`${i18nRoot}.parameters.editButton`)} relationship="label" withArrow>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<EditRegular />}
                      aria-label={t(`${i18nRoot}.parameters.editButton`)}
                      onClick={() => setActiveDateTo(true)}
                    />
                  </Tooltip>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Status ── */}
        <div className="infoPanel-section">
          <span className="infoPanel-icon" aria-hidden="true"><TagRegular /></span>
          <div className="infoPanel-body">
            <span className="infoPanel-eyebrow">{t(`${i18nRoot}.state`)}</span>
            <div className="infoPanel-row">
              <span className={`priceListStatus pricelist ${status.status}`}>{statusLabel}</span>
            </div>
          </div>
        </div>

        {/* ── Expiration ── */}
        <div className="infoPanel-section">
          <span className="infoPanel-icon" aria-hidden="true"><HourglassRegular /></span>
          <div className="infoPanel-body">
            <span className="infoPanel-eyebrow">{t(`${i18nRoot}.expiration`)}</span>
            <div className="infoPanel-row">
              {priceList.effectiveDateEnd ? (
                <span className="infoPanel-value">
                  <Moment to={priceList.effectiveDateEnd} unit="days">
                    {today}
                  </Moment>
                </span>
              ) : (
                <span className="infoPanel-value muted">{t(`${i18nRoot}.undetermined`)}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceListInfoPanel;
